import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Link from "@mui/material/Link"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { Link as RouterLink } from "react-router-dom"

import CampaignLinksStatus from "@components/campaign_links_status"
import CampaignLinksTable from "@components/campaign_links_table"
import useTranslate from "@hooks/use_translate"
import {
  type CampaignLinksToggleTarget,
  type CampaignTrackingLink
} from "@models/campaign_links"

export default function LookupResult({
  result,
  canEdit,
  onToggle
}: {
  result: CampaignTrackingLink
  canEdit: boolean
  onToggle: (target: CampaignLinksToggleTarget, disable: boolean) => void
}) {
  const { translate } = useTranslate()
  const customerLabel = result.customerName || result.customerId

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="subtitle2" color="text.secondary">
              {translate("customer")}
            </Typography>
            <Link component={RouterLink} to={`/customers/${result.customerId}`}>
              {customerLabel}
            </Link>
            <CampaignLinksStatus status={result.customerLinks} />
            {canEdit && (
              <Button
                size="small"
                variant="outlined"
                color={result.customerLinks.disabled ? "primary" : "error"}
                onClick={() =>
                  onToggle(
                    {
                      kind: "customer",
                      customerId: result.customerId,
                      label: customerLabel
                    },
                    !result.customerLinks.disabled
                  )
                }
              >
                {translate(
                  result.customerLinks.disabled
                    ? "campaign_links_turn_on_all"
                    : "campaign_links_turn_off_all"
                )}
              </Button>
            )}
          </Stack>

          {result.kind === "CAMPAIGN" && !!result.campaign && (
            <CampaignLinksTable
              rows={[result.campaign]}
              canEdit={canEdit}
              onToggle={(row) =>
                onToggle(
                  {
                    kind: "campaign",
                    campaignId: row._id,
                    label: row.title || row._id
                  },
                  !row.links.disabled
                )
              }
            />
          )}

          {result.kind === "TEMPLATE" && (
            <Stack gap={0.5}>
              <Typography variant="body2">
                {translate("campaign_links_template_link", {
                  name: result.templateName || result.templateId
                })}
              </Typography>
              {!!result.destinationUrl && (
                <Link
                  href={result.destinationUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ wordBreak: "break-all" }}
                >
                  {result.destinationUrl}
                </Link>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
